import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { useTopicsMultiSelect } from "@circle-react/components/QuickPostV3/hooks/useTopicsMultiSelect";
import { Button } from "@circleco/compass/components/Button";
import { MultiSelect } from "@circleco/compass/components/MultiSelect";
import { Typography } from "@circleco/compass/components/Typography";
import { useController, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

export const TopicsTrigger = () => {
  const { spaceId, canManageSelectedSpace } = useComposerSpace();
  const { control } = useFormContext();
  const { field } = useController({ control, name: "topics" });
  const selectedIds = (field.value as number[] | undefined) ?? [];

  const {
    createTopic,
    isCreatable,
    dropdownOptions,
    handleValueChange,
    hasMaxTopicsSelected,
    isLoading,
    onInputValueChange,
    selectedOptions,
    shouldShow,
  } = useTopicsMultiSelect({
    spaceId: spaceId ? String(spaceId) : undefined,
    value: selectedIds,
    onChange: field.onChange,
    isCreatable: canManageSelectedSpace,
  });

  if (!spaceId || !shouldShow) return null;

  // Selected topics stay in the list so they can be deselected. Compass blocks
  // opening the popup at `maxSelections`, so the limit is enforced by
  // disabling the remaining options instead.
  const options = [...selectedOptions, ...dropdownOptions].map(option =>
    hasMaxTopicsSelected && !selectedIds.includes(option.value)
      ? { ...option, disabled: true }
      : option,
  );

  return (
    <div className="shrink-0">
      <MultiSelect<number>
        data-testid="topics-list"
        trigger={
          <Button
            variant="outline"
            endIcon="chevron-triangle-down"
            data-testid="topics-trigger"
          >
            <Typography
              variant="label-sm"
              color={selectedOptions.length ? "primary" : "secondary"}
              component="span"
            >
              {selectedOptions.length
                ? t("quick_post_v3.topics_count", {
                    count: selectedOptions.length,
                  })
                : t("quick_post_v3.add_topics")}
            </Typography>
          </Button>
        }
        align="end"
        options={options}
        value={selectedOptions}
        onValueChange={handleValueChange}
        onInputValueChange={onInputValueChange}
        isLoading={isLoading}
        searchPlaceholder={t("search_v2.filters.search_topics")}
        emptyMessage={t("quick_post_v2.topics.no_results")}
        creatable={isCreatable}
        onCreateOption={name => createTopic(name)}
        loadingMessage={t("quick_post_v2.topics.loading")}
      />
    </div>
  );
};
